import { ClipboardCheck, ShieldCheck, Target } from "lucide-react";
import { useMemo } from "react";
import { usePermissions } from "@/modules/auth/presentation/model/usePermissions";
import { useChecklistExecutionStats } from "@/modules/checklist/presentation/api/queries";
import { useKPIOverview } from "@/modules/kpi/presentation/api/queries";
import { useQCResultStats } from "@/modules/qc/presentation/api/queries";
import { Permissions } from "@/modules/roles/domain/contracts/permissions";
import { formatDateForInput } from "@/shared/lib/date-time";
import { dashboardLayout } from "../lib/dashboard-layout";
import { DashboardSection } from "./DashboardSection";
import { DashboardStatCard } from "./DashboardStatCard";
import { DashboardStatMetricGrid } from "./DashboardStatMetricGrid";

export function DashboardStandardsPanel() {
  const permissions = usePermissions();

  const canKpi = permissions.hasAny([
    Permissions.mgmt_okr_kpi.read,
    Permissions.kpi.read,
  ]);
  const canChecklist = permissions.has(Permissions.checklists.read);
  const canQcResult = permissions.has(Permissions.qc_result.read);

  const now = new Date();
  const kpiFilter = useMemo(
    () => ({
      periodType: "monthly" as const,
      year: now.getFullYear(),
      month: now.getMonth() + 1,
    }),
    [now.getFullYear(), now.getMonth()],
  );
  const { data: kpiData, isLoading: kpiLoading } = useKPIOverview(kpiFilter, {
    enabled: canKpi,
  });

  const rangeQuery = useMemo(() => {
    const end = formatDateForInput(now);
    const start = formatDateForInput(
      new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000),
    );
    return {
      startDate: `${start}T00:00:00.000Z`,
      endDate: `${end}T23:59:59.999Z`,
    };
  }, [now]);

  const { data: checklistStats, isLoading: checklistLoading } =
    useChecklistExecutionStats(rangeQuery, { enabled: canChecklist });

  const { data: qcStats, isLoading: qcLoading } = useQCResultStats(
    rangeQuery,
    { enabled: canQcResult },
  );

  if (!canKpi && !canChecklist && !canQcResult) return null;

  const org = kpiData?.organization;
  const checklistOverall = checklistStats?.overall;
  const qcOverall = qcStats?.overall;

  const checklistDoneRate =
    checklistOverall && checklistOverall.total > 0
      ? Math.round(
          (checklistOverall.completed / checklistOverall.total) * 100,
        )
      : 0;

  const qcPassRate =
    qcOverall && qcOverall.total > 0
      ? Math.round((qcOverall.pass / qcOverall.total) * 100)
      : 0;

  return (
    <DashboardSection title="ມາດຕະຖານ & KPI">
      <div className={dashboardLayout.standardsCardGrid}>
        {canKpi ? (
          <DashboardStatCard
            title="KPI ເດືອນນີ້"
            subtitle="ພາບລວມລະດັບອົງກອນ"
            icon={Target}
            accent="violet"
            to="/app/management/okr-kpi"
            footer="ເບິ່ງພາບລວມ KPI"
            highlight={
              org
                ? {
                    label: "ຜ່ານ",
                    value: `${org.passRate.toFixed(0)}%`,
                  }
                : undefined
            }
          >
            {kpiLoading ? (
              <DashboardStatMetricGrid loading metrics={[]} />
            ) : org ? (
              <DashboardStatMetricGrid
                metrics={[
                  { label: "ທັງໝົດ", value: org.total },
                  { label: "ຜ່ານເປົ້າ", value: org.pass, tone: "success" },
                  { label: "ຕໍ່າກວ່າເປົ້າ", value: org.fail, tone: "danger" },
                  { label: "ລໍຖ້າປະເມີນ", value: org.pending, tone: "muted" },
                ]}
              />
            ) : (
              <p className="text-muted-foreground text-sm">ບໍ່ມີຂໍ້ມູນ</p>
            )}
          </DashboardStatCard>
        ) : null}

        {canChecklist ? (
          <DashboardStatCard
            title="Checklist"
            subtitle="30 ວັນຜ່ານມາ"
            icon={ClipboardCheck}
            accent="emerald"
            to="/app/checklists/result"
            footer="ເບິ່ງສະຫຼຸບ Checklist"
            highlight={
              checklistOverall && checklistOverall.total > 0
                ? {
                    label: "ສຳເລັດ",
                    value: `${checklistDoneRate}%`,
                  }
                : undefined
            }
          >
            {checklistLoading ? (
              <DashboardStatMetricGrid loading columns={3} metrics={[]} />
            ) : checklistOverall ? (
              <DashboardStatMetricGrid
                columns={3}
                metrics={[
                  { label: "ທັງໝົດ", value: checklistOverall.total },
                  {
                    label: "ສຳເລັດ",
                    value: checklistOverall.completed,
                    tone: "success",
                  },
                  {
                    label: "ກຳລັງທຳ",
                    value: checklistOverall.inProgress,
                    tone: "warning",
                  },
                ]}
              />
            ) : (
              <p className="text-muted-foreground text-sm">ບໍ່ມີຂໍ້ມູນ</p>
            )}
          </DashboardStatCard>
        ) : null}

        {canQcResult ? (
          <DashboardStatCard
            title="ຜົນກວດ QC"
            subtitle="30 ວັນຜ່ານມາ"
            icon={ShieldCheck}
            accent="sky"
            to="/app/qc/result"
            footer="ເບິ່ງສະຫຼຸບ QC"
            highlight={
              qcOverall && qcOverall.total > 0
                ? {
                    label: "ຜ່ານ",
                    value: `${qcPassRate}%`,
                  }
                : undefined
            }
          >
            {qcLoading ? (
              <DashboardStatMetricGrid loading columns={3} metrics={[]} />
            ) : qcOverall ? (
              <DashboardStatMetricGrid
                columns={3}
                metrics={[
                  { label: "ທັງໝົດ", value: qcOverall.total },
                  { label: "ຜ່ານ", value: qcOverall.pass, tone: "success" },
                  { label: "ບໍ່ຜ່ານ", value: qcOverall.fail, tone: "danger" },
                ]}
              />
            ) : (
              <p className="text-muted-foreground text-sm">ບໍ່ມີຂໍ້ມູນ</p>
            )}
          </DashboardStatCard>
        ) : null}
      </div>
    </DashboardSection>
  );
}
